import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePlanStore } from '@/store/planStore';
import { createCheckoutSession, createPortalSession } from '@/api/billing';
import { useIsMobile } from '@/hooks/useMediaQuery';
import {
  STRIPE_HOBBY_MONTHLY_PRICE_ID,
  STRIPE_HOBBY_ANNUAL_PRICE_ID,
  STRIPE_PRO_MONTHLY_PRICE_ID,
  STRIPE_PRO_ANNUAL_PRICE_ID,
  STRIPE_TEAM_MONTHLY_PRICE_ID,
  STRIPE_TEAM_ANNUAL_PRICE_ID,
} from '@/api/config';

type PlanTier = 'free' | 'hobby' | 'pro' | 'team';
type BillingInterval = 'monthly' | 'annual';

interface TierInfo {
  id: PlanTier;
  nameKey: string;
  taglineKey: string;
  monthlyPrice: string;
  annualPrice: string;
  annualPriceNote: string;
  featureKeys: string[];
  monthlyPriceId: string;
  annualPriceId: string;
  highlight?: boolean;
}

const tiers: TierInfo[] = [
  {
    id: 'free',
    nameKey: 'pricing.free',
    taglineKey: 'pricing.freeTagline',
    monthlyPrice: '$0',
    annualPrice: '$0',
    annualPriceNote: 'pricing.freeNote',
    monthlyPriceId: '',
    annualPriceId: '',
    featureKeys: [
      'pricing.features.upToMaps',
      'pricing.features.nodesPerMap',
      'pricing.features.horizontalOnly',
      'pricing.features.defaultTheme',
      'pricing.features.basicExport',
      'pricing.features.viewOnlySharing',
    ],
  },
  {
    id: 'hobby',
    nameKey: 'pricing.hobby',
    taglineKey: 'pricing.hobbyTagline',
    monthlyPrice: '$2.99',
    annualPrice: '$1.99',
    annualPriceNote: '/mo billed annually ($23.88/yr)',
    monthlyPriceId: STRIPE_HOBBY_MONTHLY_PRICE_ID,
    annualPriceId: STRIPE_HOBBY_ANNUAL_PRICE_ID,
    highlight: true,
    featureKeys: [
      'pricing.features.upToMaps',
      'pricing.features.nodesPerMap',
      'pricing.features.allLayouts',
      'pricing.features.allThemes',
      'pricing.features.pdfSvgExport',
      'pricing.features.attachmentsPerMap',
      'pricing.features.versionHistory',
    ],
  },
  {
    id: 'pro',
    nameKey: 'pricing.pro',
    taglineKey: 'pricing.proTagline',
    monthlyPrice: '$9',
    annualPrice: '$6',
    annualPriceNote: '/mo billed annually ($72/yr)',
    monthlyPriceId: STRIPE_PRO_MONTHLY_PRICE_ID,
    annualPriceId: STRIPE_PRO_ANNUAL_PRICE_ID,
    featureKeys: [
      'pricing.features.unlimitedMapsNodes',
      'pricing.features.unlimitedAttachments',
      'pricing.features.realtimeCollab',
      'pricing.features.editSharing',
      'pricing.features.publishMaps',
      'pricing.features.versionHistory',
      'pricing.features.allExportFormats',
    ],
  },
  {
    id: 'team',
    nameKey: 'pricing.team',
    taglineKey: 'pricing.teamTagline',
    monthlyPrice: '$6',
    annualPrice: '$4',
    annualPriceNote: '/seat/mo billed annually ($48/seat/yr)',
    monthlyPriceId: STRIPE_TEAM_MONTHLY_PRICE_ID,
    annualPriceId: STRIPE_TEAM_ANNUAL_PRICE_ID,
    featureKeys: [
      'pricing.features.everythingInPro',
      'pricing.features.teamWorkspaces',
      'pricing.features.guestAccess',
      'pricing.features.adminControls',
      'pricing.features.yearVersionHistory',
      'pricing.features.prioritySupport',
      'pricing.features.ssoComingSoon',
    ],
  },
];

// Feature interpolation parameters per feature key per tier
const featureParams: Record<string, Record<string, Record<string, string | number>>> = {
  free: {
    'pricing.features.upToMaps': { count: 5 },
    'pricing.features.nodesPerMap': { count: 100 },
  },
  hobby: {
    'pricing.features.upToMaps': { count: 25 },
    'pricing.features.nodesPerMap': { count: 500 },
    'pricing.features.attachmentsPerMap': { count: 5 },
    'pricing.features.versionHistory': { days: 30 },
  },
  pro: {
    'pricing.features.versionHistory': { days: 90 },
  },
};

const styles = {
  page: {
    width: '100vw',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    background: 'var(--bg-app)',
    fontFamily: 'var(--font-family)',
    paddingTop: 60,
    paddingBottom: 60,
    boxSizing: 'border-box' as const,
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: 'var(--text-link)',
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    fontFamily: 'var(--font-family)',
    padding: 0,
    marginBottom: 32,
    textDecoration: 'underline' as const,
    alignSelf: 'flex-start' as const,
    marginLeft: 'calc(50% - 480px)',
  },
  title: {
    color: 'var(--text-primary)',
    fontSize: 32,
    fontWeight: 700,
    marginBottom: 8,
    marginTop: 0,
    textAlign: 'center' as const,
  },
  subtitle: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--font-size-lg)',
    marginBottom: 40,
    marginTop: 0,
    textAlign: 'center' as const,
  },
  grid: {
    display: 'flex',
    gap: 24,
    flexWrap: 'wrap' as const,
    justifyContent: 'center',
    maxWidth: 1240,
    width: '100%',
    padding: '0 16px',
    boxSizing: 'border-box' as const,
  },
  card: {
    background: 'var(--bg-surface)',
    borderRadius: 'var(--radius-lg)',
    padding: '32px 28px',
    width: 280,
    boxShadow: 'var(--shadow-md)',
    display: 'flex',
    flexDirection: 'column' as const,
    position: 'relative' as const,
  },
  cardHighlight: {
    border: '2px solid var(--color-primary)',
  },
  cardDefault: {
    border: '1px solid var(--border-default)',
  },
  badge: {
    position: 'absolute' as const,
    top: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'var(--color-primary)',
    color: '#ffffff',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 700,
    padding: '4px 12px',
    borderRadius: 'var(--radius-lg)',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  tierName: {
    color: 'var(--text-primary)',
    fontSize: 20,
    fontWeight: 700,
    marginBottom: 4,
    marginTop: 0,
  },
  tagline: {
    color: 'var(--text-secondary)',
    fontSize: 'var(--font-size-sm)',
    marginBottom: 20,
    marginTop: 0,
  },
  price: {
    color: 'var(--text-primary)',
    fontSize: 36,
    fontWeight: 800,
    marginBottom: 2,
  },
  priceNote: {
    color: 'var(--text-tertiary)',
    fontSize: 'var(--font-size-sm)',
    marginBottom: 24,
  },
  featureList: {
    listStyle: 'none' as const,
    padding: 0,
    margin: 0,
    flex: 1,
    marginBottom: 24,
  },
  featureItem: {
    color: '#cbd5e1',
    fontSize: 'var(--font-size-sm)',
    padding: '6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  checkmark: {
    color: 'var(--color-success)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 700,
    flexShrink: 0,
  },
  button: {
    padding: '10px 16px',
    background: 'var(--color-primary)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-base)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family)',
    width: '100%',
    textAlign: 'center' as const,
  },
  buttonSecondary: {
    background: 'var(--bg-surface-raised)',
    border: '1px solid var(--border-strong)',
  },
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed' as const,
  },
  error: {
    background: 'var(--bg-error)',
    color: 'var(--text-error)',
    padding: '10px 12px',
    borderRadius: 6,
    fontSize: 'var(--font-size-sm)',
    marginBottom: 16,
    textAlign: 'center' as const,
    maxWidth: 1240,
    width: '100%',
  },
};

interface PricingPageProps {
  onBack: () => void;
}

export function PricingPage({ onBack }: PricingPageProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const currentPlan = usePlanStore((s) => s.plan);
  const status = usePlanStore((s) => s.status);
  const [loading, setLoading] = useState<PlanTier | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<BillingInterval>('annual');
  const [teamSeats, setTeamSeats] = useState(3);

  const TEAM_MIN_SEATS = 3;

  async function handleUpgrade(tier: TierInfo) {
    const priceId = billingInterval === 'annual' ? tier.annualPriceId : tier.monthlyPriceId;
    if (!priceId) return;
    const quantity = tier.id === 'team' ? Math.max(teamSeats, TEAM_MIN_SEATS) : 1;
    setLoading(tier.id);
    setError(null);
    try {
      await createCheckoutSession(priceId, quantity);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start checkout';
      setError(message);
      setLoading(null);
    }
  }

  async function handleManage() {
    setLoading(currentPlan);
    setError(null);
    try {
      await createPortalSession();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to open billing portal';
      setError(message);
      setLoading(null);
    }
  }

  function getTierOrder(tier: PlanTier): number {
    if (tier === 'free') return 0;
    if (tier === 'hobby') return 1;
    if (tier === 'pro') return 2;
    return 3;
  }

  return (
    <div style={styles.page}>
      <button style={{
        ...styles.backButton,
        marginLeft: isMobile ? 16 : 'calc(50% - 480px)',
      }} onClick={onBack}>
        {t('pricing.backToEditor')}
      </button>

      <h1 style={styles.title}>{t('pricing.chooseYourPlan')}</h1>
      <p style={styles.subtitle}>
        {t('pricing.subtitle')}
      </p>

      {/* Billing interval toggle */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 32,
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-md)',
        padding: 4,
      }}>
        <button
          onClick={() => setBillingInterval('monthly')}
          style={{
            background: billingInterval === 'monthly' ? 'var(--bg-surface-raised)' : 'transparent',
            color: billingInterval === 'monthly' ? 'var(--text-primary)' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: 6,
            padding: '8px 16px',
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          {t('pricing.monthly')}
        </button>
        <button
          onClick={() => setBillingInterval('annual')}
          style={{
            background: billingInterval === 'annual' ? 'var(--bg-surface-raised)' : 'transparent',
            color: billingInterval === 'annual' ? 'var(--text-primary)' : 'var(--text-secondary)',
            border: 'none',
            borderRadius: 6,
            padding: '8px 16px',
            fontSize: 'var(--font-size-base)',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          {t('pricing.annual')}
          <span style={{ color: 'var(--color-success)', fontSize: 'var(--font-size-xs)', marginLeft: 6 }}>{t('pricing.saveUpTo')}</span>
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={{
        ...styles.grid,
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'center' : undefined,
        padding: isMobile ? '0 12px' : '0 16px',
      }}>
        {tiers.map((tier) => {
          const isCurrent = tier.id === currentPlan && (status !== 'none' || tier.id === 'free');
          const isHigher = getTierOrder(tier.id) > getTierOrder(currentPlan);
          const isLower = getTierOrder(tier.id) < getTierOrder(currentPlan);
          const isLoading = loading === tier.id;

          return (
            <div
              key={tier.id}
              style={{
                ...styles.card,
                ...(isCurrent ? styles.cardHighlight : tier.highlight ? { border: '2px solid var(--color-secondary)' } : styles.cardDefault),
                width: isMobile ? '100%' : 280,
                maxWidth: isMobile ? '100%' : undefined,
              }}
            >
              {isCurrent && <div style={styles.badge}>{t('pricing.currentPlan')}</div>}
              {!isCurrent && tier.highlight && (
                <div style={{ ...styles.badge, background: 'var(--color-secondary)' }}>{t('pricing.mostPopular')}</div>
              )}

              <h2 style={styles.tierName}>{t(tier.nameKey)}</h2>
              <p style={styles.tagline}>{t(tier.taglineKey)}</p>

              <div style={styles.price}>
                {tier.id === 'free' ? t('pricing.freePrice') : billingInterval === 'annual' ? tier.annualPrice : tier.monthlyPrice}
              </div>
              <div style={styles.priceNote}>
                {tier.id === 'free' ? t('pricing.freeNote') : (
                  billingInterval === 'annual' ? tier.annualPriceNote : t('pricing.perMonth')
                )}
              </div>

              <ul style={styles.featureList}>
                {tier.featureKeys.map((featureKey) => {
                  const params = featureParams[tier.id]?.[featureKey];
                  return (
                    <li key={featureKey} style={styles.featureItem}>
                      <span style={styles.checkmark}>&#10003;</span>
                      {params ? t(featureKey, params) : t(featureKey)}
                    </li>
                  );
                })}
              </ul>

              {/* Seat selector for Team plan */}
              {tier.id === 'team' && !isCurrent && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  marginBottom: 16,
                  padding: '8px 0',
                }}>
                  <button
                    onClick={() => setTeamSeats(Math.max(TEAM_MIN_SEATS, teamSeats - 1))}
                    style={{
                      background: 'var(--bg-surface-raised)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-strong)',
                      borderRadius: 6,
                      width: 28,
                      height: 28,
                      cursor: teamSeats <= TEAM_MIN_SEATS ? 'not-allowed' : 'pointer',
                      opacity: teamSeats <= TEAM_MIN_SEATS ? 0.4 : 1,
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }}
                    disabled={teamSeats <= TEAM_MIN_SEATS}
                  >
                    -
                  </button>
                  <span style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-base)', fontWeight: 600, minWidth: 60, textAlign: 'center' as const }}>
                    {t('pricing.seats', { count: teamSeats })}
                  </span>
                  <button
                    onClick={() => setTeamSeats(teamSeats + 1)}
                    style={{
                      background: 'var(--bg-surface-raised)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-strong)',
                      borderRadius: 6,
                      width: 28,
                      height: 28,
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-lg)',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                    }}
                  >
                    +
                  </button>
                </div>
              )}
              {tier.id === 'team' && !isCurrent && (
                <div style={{ color: 'var(--text-secondary)', fontSize: 12, textAlign: 'center' as const, marginBottom: 12 }}>
                  {billingInterval === 'annual'
                    ? `$${(teamSeats * 4).toFixed(0)}/mo ($${(teamSeats * 48).toFixed(0)}/yr)`
                    : `$${(teamSeats * 6).toFixed(0)}/mo`
                  }
                  {' '} total &middot; {t('pricing.minSeats', { count: TEAM_MIN_SEATS })}
                </div>
              )}

              {isCurrent ? (
                <button
                  style={{
                    ...styles.button,
                    ...styles.buttonSecondary,
                    ...(isLoading ? styles.buttonDisabled : {}),
                  }}
                  disabled={isLoading}
                  onClick={() => void handleManage()}
                >
                  {isLoading ? t('common.loading') : t('pricing.manageSubscription')}
                </button>
              ) : isHigher ? (
                <button
                  style={{
                    ...styles.button,
                    ...(isLoading ? styles.buttonDisabled : {}),
                  }}
                  disabled={isLoading}
                  onClick={() => void handleUpgrade(tier)}
                >
                  {isLoading ? t('common.loading') : t('common.upgrade')}
                </button>
              ) : isLower ? (
                <button
                  style={{
                    ...styles.button,
                    ...styles.buttonSecondary,
                    ...(isLoading ? styles.buttonDisabled : {}),
                  }}
                  disabled={isLoading}
                  onClick={() => void handleManage()}
                >
                  {isLoading ? t('common.loading') : t('pricing.downgrade')}
                </button>
              ) : (
                // Free tier when user has no subscription
                <button
                  style={{ ...styles.button, ...styles.buttonSecondary }}
                  disabled
                >
                  {t('pricing.currentPlan')}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
